// Sugalarm — Cycle 227: a height-bounded, scrollbar-free scroll region
// with DYNAMIC edge fades driven by scroll position.
//
//   • No visible scrollbar on any platform (CSS in the module).
//   • A top fade appears only once scrolled away from the top; a bottom
//     fade only while more content remains below. A short (non-overflowing)
//     region shows NO fade at all. Driven by a lightweight scroll handler
//     toggling the `mask-image`, recomputed on content change and resize.
//
// The mechanics are self-contained; the consumer supplies the height bound
// + flex sizing via `className` (so the same component can size differently
// per surface). Used by the mobile-portrait IOB card's detail region — the
// fixed rings/center stay outside this; only the MealContextLines scroll.

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import styles from "./MaskedScroll.module.css";

interface Props {
  /** Height bound + flex sizing (a CSS-module class from the consumer). */
  className?: string;
  children: ReactNode;
}

// Edge-fade depth in px. Sized so an overflowing region leaves a partial
// row/section peeking under the bottom fade as the "more below" affordance.
const FADE = 18;

export function MaskedScroll({ className, children }: Props) {
  const ref = useRef<HTMLDivElement | null>(null);
  // Both true at rest (no overflow → no fade at either edge).
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const recompute = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflowing = scrollHeight - clientHeight > 1;
    setAtTop(!overflowing || scrollTop <= 1);
    setAtBottom(!overflowing || scrollTop >= scrollHeight - clientHeight - 1);
  }, []);

  // Recompute on mount, on content-size change, and on resize — a
  // ResizeObserver on the region AND its content catches both the bound
  // shrinking (short screens) and the detail growing/shrinking (a dose
  // logged/cleared). `children` in deps re-attaches when the content node
  // identity changes.
  useEffect(() => {
    recompute();
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(recompute);
    ro.observe(el);
    for (const child of Array.from(el.children)) ro.observe(child);
    return () => ro.disconnect();
  }, [recompute, children]);

  // Dynamic mask: a top fade only when scrolled off the top, a bottom fade
  // only while more remains below; both clear at their extremes so a
  // non-overflowing region shows none.
  const topStop = atTop ? "0px" : `${FADE}px`;
  const bottomStop = atBottom ? "100%" : `calc(100% - ${FADE}px)`;
  const mask =
    atTop && atBottom
      ? undefined
      : `linear-gradient(to bottom, transparent 0, #000 ${topStop}, #000 ${bottomStop}, transparent 100%)`;

  return (
    <div
      ref={ref}
      className={`${styles.scroll} ${className ?? ""}`.trim()}
      onScroll={recompute}
      style={mask ? { maskImage: mask, WebkitMaskImage: mask } : undefined}
    >
      {children}
    </div>
  );
}
